// src/components/pages/message/timecapsule/Step1Compose.js
import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Typography,
  Card,
  IconButton,
  Snackbar,
  Alert,
  Button,
  InputAdornment,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";

import IconNextArrow from "../../../../assets/icons/icon-next-arrow.svg";
import IconNewMedia from "../../../../assets/icons/icon-new-media.svg";
import IconMyGallery from "../../../../assets/icons/icon-my-gallery.svg";
import { ReactComponent as IconTrash } from "../../../../assets/icons/icon-delete.svg";

import VideoRecorderModal from "./VideoRecorderModal";
import PhotoCaptureModal from "./PhotoCaptureModal";

import MediaPreview from "./components/MediaPreview";

const VIDEO_FILE_EXTENSIONS = new Set([
  "mp4",
  "mov",
  "m4v",
  "webm",
  "ogg",
  "ogv",
  "3gp",
  "3gpp",
]);

function extFromMime(mime = "") {
  if (mime.includes("mp4")) return "mp4";
  if (mime.includes("webm")) return "webm";
  if (mime.includes("quicktime")) return "mov";
  return "webm";
}

function getFileExtension(name = "") {
  const cleanName = String(name || "").split("?")[0].split("#")[0];
  const parts = cleanName.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function looksLikeVideoFile(file) {
  if (!file) return false;
  const rawType = String(file.type || "").toLowerCase();
  if (rawType.startsWith("video/")) return true;
  if (rawType.startsWith("image/")) return false;
  return VIDEO_FILE_EXTENSIONS.has(getFileExtension(file.name));
}

function normalizePickedFileType(file) {
  const rawType = String(file?.type || "").trim().toLowerCase();
  if (rawType) return rawType;
  return looksLikeVideoFile(file) ? "video/mp4" : "image/jpeg";
}

function revokeIfBlobUrl(url) {
  try {
    if (typeof url === "string" && url.startsWith("blob:")) URL.revokeObjectURL(url);
  } catch {
    // ignore
  }
}

// Best-effort duration read without fully loading the file
async function getVideoDurationSeconds(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    const url = URL.createObjectURL(file);

    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
      video.removeAttribute("src");
      video.load();
    };

    const done = (duration) => {
      cleanup();
      resolve(duration);
    };

    video.onerror = () => {
      cleanup();
      reject(new Error("Failed to read video metadata"));
    };

    video.onloadedmetadata = () => {
      // Some browsers report Infinity until a seek occurs
      if (Number.isFinite(video.duration) && video.duration > 0) {
        done(video.duration);
        return;
      }

      // Infinity/NaN workaround: seek far to force duration
      video.ontimeupdate = () => {
        video.ontimeupdate = null;
        if (Number.isFinite(video.duration) && video.duration > 0) {
          done(video.duration);
        } else {
          cleanup();
          reject(new Error("Video duration unavailable"));
        }
      };

      try {
        video.currentTime = 1e101;
      } catch {
        cleanup();
        reject(new Error("Video duration unavailable"));
      }
    };

    video.src = url;
  });
}

const Step1Compose = ({ wizardData, setWizardData, onNext, onSaveAndExit }) => {
  const [error, setError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const initialSubjectRef = useRef(String(wizardData?.subject || ""));
  const initialMessageRef = useRef(String(wizardData?.message || ""));

  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);

  const MAX_CHAR = 3000;
  const MAX_CHAR_SUBJECT = 140;

  // Policy limit (time-only)
  const MAX_VIDEO_SECONDS = 10;

  const navigate = useNavigate();

  const isIOS = useMemo(
    () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,
    []
  );

  const canUseMediaRecorder = useMemo(() => typeof MediaRecorder !== "undefined", []);

  const actionBarBottom = useMemo(
    () => "env(safe-area-inset-bottom)",
    []
  );
  const cancelState = useMemo(() => {
    if (!Number.isFinite(wizardData?.lat) || !Number.isFinite(wizardData?.lng)) return undefined;
    return { center: { lat: wizardData.lat, lng: wizardData.lng } };
  }, [wizardData?.lat, wizardData?.lng]);

  const showSnack = (msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const handleSaveAndExit = () => {
    if (typeof onSaveAndExit === "function") {
      onSaveAndExit();
      return;
    }
    navigate("/", { state: cancelState });
  };

  const handleSubjectChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length > MAX_CHAR_SUBJECT) {
      setError(`Subject cannot exceed ${MAX_CHAR_SUBJECT} characters.`);
    } else {
      setError("");
      setWizardData((prev) => ({ ...prev, subject: newValue }));
    }
  };

  const handleMessageChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length > MAX_CHAR) {
      setMessageError(`Message cannot exceed ${MAX_CHAR} characters.`);
    } else {
      setMessageError("");
      setWizardData((prev) => ({ ...prev, message: newValue }));
    }
  };

  const validatePickedFile = async (file) => {
    if (!file) return { ok: false, reason: "No file selected." };

    const isVideo = looksLikeVideoFile(file);
    if (!isVideo) {
      // Images are always allowed
      return { ok: true };
    }

    try {
      const duration = await getVideoDurationSeconds(file);

      // Small tolerance because some devices report slightly off
      if (duration > MAX_VIDEO_SECONDS + 0.25) {
        return {
          ok: false,
          reason:
            `That video is too long. Please choose a video ` +
            `${MAX_VIDEO_SECONDS} seconds or less, or record a new one with the camera.`,
        };
      }

      return { ok: true };
    } catch (err) {
      console.warn("Duration check failed:", err);
      return {
        ok: false,
        reason:
          `We couldn’t read that video’s length. Please choose a shorter video ` +
          `(${MAX_VIDEO_SECONDS}s or less), or record a new one with the camera.`,
      };
    }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];

    // Allow selecting the same file again after rejection
    try {
      e.target.value = "";
    } catch {
      // ignore
    }

    if (!file) return;

    const validation = await validatePickedFile(file);
    if (!validation.ok) {
      showSnack(validation.reason || "Unsupported media.");
      return;
    }

    revokeIfBlobUrl(wizardData?.mediaFile?.url);

    const url = URL.createObjectURL(file);

    setWizardData((prev) => ({
      ...prev,
      mediaFile: { file, url, type: normalizePickedFileType(file) },
    }));
  };

  const handleRemoveMedia = () => {
    revokeIfBlobUrl(wizardData?.mediaFile?.url);
    setWizardData((prev) => ({ ...prev, mediaFile: null }));
  };

  const handlePhotoCapture = async (imageSrc) => {
    try {
      revokeIfBlobUrl(wizardData?.mediaFile?.url);

      const resp = await fetch(imageSrc);
      const blob = await resp.blob();
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });

      setWizardData((prev) => ({
        ...prev,
        mediaFile: { file, url: imageSrc, type: "image/jpeg" },
      }));
    } catch (err) {
      console.error("Photo capture error:", err);
      showSnack("Failed to process captured photo.");
    } finally {
      setShowPhotoCapture(false);
    }
  };

  const handleVideoRecorded = ({ blob, mimeType }) => {
    try {
      revokeIfBlobUrl(wizardData?.mediaFile?.url);

      const safeMime = mimeType || blob?.type || "video/webm";
      const ext = extFromMime(safeMime);

      const newUrl = URL.createObjectURL(blob);
      const file =
        blob instanceof Blob
          ? new File([blob], `video_${Date.now()}.${ext}`, { type: safeMime })
          : null;

      setWizardData((prev) => ({
        ...prev,
        mediaFile: { file, url: newUrl, type: safeMime },
      }));
    } catch (err) {
      console.error("Video record error:", err);
      showSnack("Failed to process recorded video.");
    } finally {
      setShowVideoRecorder(false);
    }
  };

  const handleNextStep = () => {
    if (!wizardData.message?.trim()) {
      showSnack("Message is required.");
      return;
    }
    onNext();
  };

  const handleClearSubject = () => {
    setError("");
    setWizardData((prev) => ({ ...prev, subject: "" }));
  };

  const handleRestoreSubject = () => {
    setError("");
    setWizardData((prev) => ({ ...prev, subject: initialSubjectRef.current }));
  };

  const handleClearMessage = () => {
    setMessageError("");
    setWizardData((prev) => ({ ...prev, message: "" }));
  };

  const handleRestoreMessage = () => {
    setMessageError("");
    setWizardData((prev) => ({ ...prev, message: initialMessageRef.current }));
  };

  const showRestoreSubject = !String(wizardData.subject || "") && Boolean(initialSubjectRef.current);
  const showRestoreMessage = !String(wizardData.message || "") && Boolean(initialMessageRef.current);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ pt: 2, pb: "calc(env(safe-area-inset-bottom) + 110px)" }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
        Step 1: Compose
      </Typography>

      <Box sx={{ mb: 1.5 }}>
        <TextField
          label="Subject*"
          fullWidth
          value={wizardData.subject}
          onChange={handleSubjectChange}
          error={Boolean(error)}
          helperText={error || `${wizardData.subject.length}/${MAX_CHAR_SUBJECT}`}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={showRestoreSubject ? handleRestoreSubject : handleClearSubject}
                    disabled={!showRestoreSubject && !String(wizardData.subject || "")}
                    aria-label={showRestoreSubject ? "Restore subject" : "Clear subject"}
                    edge="end"
                    sx={(theme) => ({
                      width: 28,
                      height: 28,
                      border: "1px solid",
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    })}
                  >
                    {showRestoreSubject ? <UndoRoundedIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Box sx={{ position: "relative", mt: 1, mb: 1 }}>
        <TextField
          label="Your Message*"
          multiline
          minRows={4}
          fullWidth
          value={wizardData.message}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={handleMessageChange}
          error={Boolean(messageError)}
          helperText={messageError || `${wizardData.message.length}/${MAX_CHAR}`}
          sx={{
            "& .MuiOutlinedInput-input": {
              pr: 5,
              pb: 5,
            },
          }}
        />
        <IconButton
          size="small"
          onClick={showRestoreMessage ? handleRestoreMessage : handleClearMessage}
          disabled={!showRestoreMessage && !String(wizardData.message || "")}
          aria-label={showRestoreMessage ? "Restore message" : "Clear message"}
          sx={(theme) => ({
            position: "absolute",
            bottom: 34,
            right: 10,
            width: 28,
            height: 28,
            border: "1px solid",
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.secondary,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          })}
        >
          {showRestoreMessage ? <UndoRoundedIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
        </IconButton>
      </Box>

      {wizardData.mediaFile && (
        <Card sx={{ mb: 2, position: "relative", mt: 2 }} elevation={0}>
          <MediaPreview mediaFile={wizardData.mediaFile} outerSx={{ mb: 0, mt: 0 }} />

          <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}>
            <IconButton
              onClick={handleRemoveMedia}
              sx={(theme) => ({
                width: 50,
                height: 50,
                borderRadius: "999px",
                backgroundColor: theme.colors.red,
                color: theme.colors.white,
                "&:hover": { backgroundColor: theme.colors.darkBlue },
              })}
            >
              <IconTrash width={24} height={24} />
            </IconButton>
          </Box>
        </Card>
      )}

      {!wizardData.mediaFile && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3, mt: 3 }}>
          {isIOS ? (
            <>
              <Button
                variant="contained"
                onClick={() => setShowPhotoCapture(true)}
                startIcon={<Box component="img" src={IconNewMedia} alt="" sx={{ width: 24, height: 24 }} />}
                sx={(theme) => ({
                  flex: 1,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.colors.blue,
                  border: `1px solid ${theme.colors.blue}`,
                  padding: "10px 20px",
                  fontSize: "1rem",
                })}
              >
                New Photo
              </Button>

              {canUseMediaRecorder ? (
                <Button
                  variant="contained"
                  onClick={() => setShowVideoRecorder(true)}
                  startIcon={<Box component="img" src={IconNewMedia} alt="" sx={{ width: 24, height: 24 }} />}
                  sx={(theme) => ({
                    flex: 1,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.colors.blue,
                    border: `1px solid ${theme.colors.blue}`,
                    padding: "10px 20px",
                    fontSize: "1rem",
                  })}
                >
                  New Video
                </Button>
              ) : (
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<Box component="img" src={IconNewMedia} alt="" sx={{ width: 24, height: 24 }} />}
                  sx={(theme) => ({
                    flex: 1,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.colors.blue,
                    border: `1px solid ${theme.colors.blue}`,
                    padding: "10px 20px",
                    fontSize: "1rem",
                  })}
                >
                  New Video
                  <input
                    hidden
                    type="file"
                    accept="video/*"
                    capture="environment"
                    onChange={handleMediaUpload}
                  />
                </Button>
              )}

              <Button
                variant="contained"
                component="label"
                startIcon={<Box component="img" src={IconMyGallery} alt="" sx={{ width: 24, height: 24 }} />}
                sx={(theme) => ({
                  flex: 1,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.colors.blue,
                  border: `1px solid ${theme.colors.blue}`,
                  padding: "10px 20px",
                  fontSize: "1rem",
                })}
              >
                My Gallery
                <input
                  hidden
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleMediaUpload}
                />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={() => setShowPhotoCapture(true)}
                startIcon={<Box component="img" src={IconNewMedia} alt="" sx={{ width: 24, height: 24 }} />}
                sx={(theme) => ({
                  flex: 1,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.colors.blue,
                  border: `1px solid ${theme.colors.blue}`,
                  padding: "10px 20px",
                  fontSize: "1rem",
                })}
              >
                New Photo
              </Button>

              <Button
                variant="contained"
                onClick={() => setShowVideoRecorder(true)}
                startIcon={<Box component="img" src={IconNewMedia} alt="" sx={{ width: 24, height: 24 }} />}
                sx={(theme) => ({
                  flex: 1,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.colors.blue,
                  border: `1px solid ${theme.colors.blue}`,
                  padding: "10px 20px",
                  fontSize: "1rem",
                })}
              >
                New Video
              </Button>

              <Button
                variant="contained"
                component="label"
                startIcon={<Box component="img" src={IconMyGallery} alt="" sx={{ width: 24, height: 24 }} />}
                sx={(theme) => ({
                  flex: 1,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.colors.blue,
                  border: `1px solid ${theme.colors.blue}`,
                  padding: "10px 20px",
                  fontSize: "1rem",
                })}
              >
                My Gallery
                <input
                  hidden
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleMediaUpload}
                />
              </Button>
            </>
          )}
        </Box>
      )}

      <PhotoCaptureModal
        open={showPhotoCapture}
        onClose={() => setShowPhotoCapture(false)}
        onCapture={handlePhotoCapture}
      />

      <VideoRecorderModal
        open={showVideoRecorder}
        onClose={() => setShowVideoRecorder(false)}
        onRecorded={handleVideoRecorded}
        maxSeconds={MAX_VIDEO_SECONDS}
      />

      {/* Fixed bottom action bar */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: actionBarBottom,
          zIndex: (theme) => theme.zIndex.appBar,
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderTopColor: "divider",
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", width: "100%" }}>
          <IconButton
            onClick={handleSaveAndExit}
            sx={(theme) => ({
              borderRadius: "999px",
              width: 48,
              height: 48,
              flex: "0 0 auto",
              border: "1px solid",
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.mode === "dark"
                ? alpha(theme.palette.text.primary, 0.02)
                : "transparent",
              color: theme.palette.text.primary,
              "&:hover": {
                boxShadow: "none",
                backgroundColor: theme.palette.mode === "dark"
                  ? alpha(theme.palette.text.primary, 0.06)
                  : alpha(theme.palette.text.primary, 0.02),
              },
            })}
            aria-label="Back and save"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Button
            variant="outlined"
            onClick={handleSaveAndExit}
            sx={(theme) => ({
              fontWeight: 700,
              px: 3,
              py: 1.25,
              flex: 1,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
            })}
          >
            Save & exit
          </Button>

          <IconButton
            onClick={handleNextStep}
            sx={(theme) => ({
              borderRadius: "999px",
              width: 48,
              height: 48,
              flex: "0 0 auto",
              backgroundColor: theme.colors.blue,
              color: theme.palette.common.white,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            })}
            aria-label="Next step"
          >
            <Box component="img" src={IconNextArrow} alt="" sx={{ width: 16, height: 16 }} />
          </IconButton>
        </Box>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          variant="filled"
          sx={(theme) => ({
            width: "100%",
            backgroundColor: theme.colors.red,
          })}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Step1Compose;
